import React from 'react';
import {
  cimmplePayComparison,
  cimmplePayPayrollYourWay,
  cimmplePayPlans,
} from '../../data/cimmplePayPricing';
import { productTheme } from '../../utils/productTheme';
import CimmplePayComparisonTable from './CimmplePayComparisonTable';
import CimmplePayPricingCards from './CimmplePayPricingCards';

interface CimmplePayPricingContentProps {
  onSelectTrial: (planName: string) => void;
}

/**
 * CimmplePay-only pricing body: plan cards, flexibility messaging, and comparison table.
 * Hero / FAQ live on the Pricing page so page chrome stays shared.
 */
const CimmplePayPricingContent: React.FC<CimmplePayPricingContentProps> = ({
  onSelectTrial,
}) => {
  const theme = productTheme('accent');

  return (
    <>
      {/* Pricing Cards */}
      <section className="pb-20" aria-label="CimmplePay pricing plans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CimmplePayPricingCards plans={cimmplePayPlans} onSelectTrial={onSelectTrial} />
        </div>
      </section>

      {/* Payroll Your Way */}
      <section className="pb-20" aria-labelledby="payroll-your-way-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto rounded-2xl border border-accent-200/60 dark:border-accent-500/20 bg-gradient-to-br from-accent-50/80 via-white to-slate-50 dark:from-accent-500/10 dark:via-dark-900/50 dark:to-dark-900/30 p-8 md:p-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 dark:bg-accent-500/10 text-accent-700 dark:text-accent-400 text-xs font-medium mb-4">
              {cimmplePayPayrollYourWay.eyebrow}
            </span>
            <h2
              id="payroll-your-way-heading"
              className="text-2xl md:text-3xl font-display font-bold text-slate-900 dark:text-white mb-5"
            >
              {cimmplePayPayrollYourWay.title}
            </h2>
            <div className="space-y-4">
              {cimmplePayPayrollYourWay.paragraphs.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-slate-600 dark:text-dark-300 leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-6 flex items-start gap-3 text-sm text-slate-500 dark:text-dark-400">
              <svg
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${theme.text}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p>
                CimmplePay is payroll management software—not a payment processor. You keep control of how employees get paid.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section
        className="py-16 bg-slate-50 dark:bg-dark-900/30"
        aria-labelledby="cimmplepay-compare-heading"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 id="cimmplepay-compare-heading" className="section-title">
              Compare <span className="gradient-text">plans</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              See what&apos;s included across Payroll Management, Workforce, HR &amp; Compliance, and Support.
            </p>
          </div>
          <div className="card p-4 sm:p-6 md:p-8">
            <CimmplePayComparisonTable categories={cimmplePayComparison} />
          </div>
        </div>
      </section>
    </>
  );
};

export default CimmplePayPricingContent;
